import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Radio, Switch, Button, Input, Dropdown, message } from 'antd';
import type { RadioChangeEvent, MenuProps } from 'antd';
import { DownloadOutlined, SearchOutlined, LoadingOutlined, RightOutlined, DownOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import RightContentDrawer from '../../../../components/common/RightContentDrawer';
import ButtonGroup from '../../../../components/common/ButtonGroup';
import { exportData, exportMultiSectionData, type ExportFormat, type ExportColumn, type ExportSection } from '../../../../utils/exportUtils';
import type { RootState } from '../../../../store';
import {
  searchCycles,
  searchProcesses,
  type CycleItem,
  type ProcessItem,
} from '../../../../services/hierarchyService';
import {
  getObjectivesFullReport,
  getCompliancePurposesFullReport,
  parseObjectives,
  parseCompliancePurposes,
} from '../../../../services/riskAssessmentService';
import CloseCircleIcon from '../../../../assets/icons/ic-close-circle.svg?react';

// ========== TYPES ==========

interface RiskAssessmentExportPanelProps {
  open: boolean;
  onClose: () => void;
}

// ========== STYLED COMPONENTS ==========

const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div``;

const SectionTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
  margin-bottom: 12px;
`;

const FormatGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormatOption = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid ${props => props.$selected ? '#2563EB' : '#E5E7EB'};
  border-radius: 8px;
  background: ${props => props.$selected ? '#EFF6FF' : '#FFFFFF'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #2563EB;
  }

  .ant-radio-wrapper {
    width: 100%;
    margin: 0;
  }
`;

const FormatLabel = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
  margin-left: 8px;
`;

const FormatExt = styled.span`
  font-family: Inter;
  font-size: 12px;
  color: #6B7280;
  margin-left: 4px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ToggleLabel = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
`;

const TagContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 32px 8px 12px;
  min-height: 44px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  background: white;
  cursor: pointer;

  &:hover {
    border-color: #2563EB;
  }
`;

const ArrowButton = styled.div`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  
  .anticon {
    color: #6B7280;
    font-size: 12px;
    transition: color 0.2s;
  }

  &:hover .anticon {
    color: #2563EB;
  }
`;

const Tag = styled.span<{ 
  $backgroundColor: string; 
  $color: string;
  $isMoreTag?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${props => props.$backgroundColor};
  border: none;
  border-radius: 50px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.$color};
  cursor: ${props => props.$isMoreTag ? 'pointer' : 'default'};
  transition: all 0.2s;

  ${props => props.$isMoreTag && `
    &:hover {
      opacity: 0.9;
    }
  `}
`;

const StyledCloseIcon = styled(CloseCircleIcon)<{ $circleFill: string; $strokeColor: string }>`
  cursor: pointer;
  flex-shrink: 0;
  
  path:first-child {
    fill: ${props => props.$circleFill};
  }
  
  path:last-child {
    stroke: ${props => props.$strokeColor};
  }
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SearchInputWrapper = styled.div`
  margin-bottom: 24px;

  .ant-input-affix-wrapper {
    height: 44px;
    border-radius: 6px;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;

    .ant-input {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    .anticon-search {
      color: #6B7280;
    }
  }
`;

const SearchResultsList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const SearchResultItem = styled.div<{ $selected: boolean }>`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#EFF6FF' : 'transparent'};
  border: 1px solid ${props => props.$selected ? '#2563EB' : '#E5E7EB'};

  &:hover {
    background: ${props => props.$selected ? '#EFF6FF' : '#F3F4F6'};
  }

  .item-label {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${props => props.$selected ? '#2563EB' : '#1F2937'};
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #9CA3AF;
  font-family: Inter;
  font-size: 14px;
`;

const FilePreview = styled.div`
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 12px 16px;
`;

const FilePreviewLabel = styled.div`
  font-family: Inter;
  font-size: 12px;
  color: #6B7280;
  margin-bottom: 4px;
`;

const FilePreviewName = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
  word-break: break-all;
`;

const FooterButtonGroup = styled(ButtonGroup)`
  margin-bottom: 0;
  
  .ant-btn {
    flex: 1;
    height: 44px;
  }
`;

const FieldLabel = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
  margin-bottom: 8px;
`;

// ========== COMPONENT ==========

const RiskAssessmentExportPanel: React.FC<RiskAssessmentExportPanelProps> = ({
  open,
  onClose,
}) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const annualReportId = selectedReport?.annualReportId;

  // Format & toggles
  const [format, setFormat] = useState<ExportFormat>('excel');
  const [includeObjectives, setIncludeObjectives] = useState(true);
  const [includeFraudRisks, setIncludeFraudRisks] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Cycle/Process selection
  const [selectedCycles, setSelectedCycles] = useState<CycleItem[]>([]);
  const [selectedProcesses, setSelectedProcesses] = useState<ProcessItem[]>([]);

  // Search mode
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchType, setSearchType] = useState<'cycle' | 'process'>('cycle');
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSelectedCycles, setTempSelectedCycles] = useState<CycleItem[]>([]);
  const [tempSelectedProcesses, setTempSelectedProcesses] = useState<ProcessItem[]>([]);
  const [cycleResults, setCycleResults] = useState<CycleItem[]>([]);
  const [processResults, setProcessResults] = useState<ProcessItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [cyclesCache, setCyclesCache] = useState<Record<string, CycleItem[]>>({});

  // Reset state when panel opens
  useEffect(() => {
    if (open) {
      setFormat('excel');
      setIncludeObjectives(true);
      setIncludeFraudRisks(true);
      setSelectedCycles([]);
      setSelectedProcesses([]);
      setIsSearchMode(false);
    }
  }, [open]);

  // ==================== SEARCH MODE ====================

  const enterSearchMode = (type: 'cycle' | 'process') => {
    setSearchType(type);
    setIsSearchMode(true);
    setSearchQuery('');
    setTempSelectedCycles([...selectedCycles]);
    setTempSelectedProcesses([...selectedProcesses]);

    if (type === 'cycle' && annualReportId && cyclesCache[annualReportId]) {
      setCycleResults(cyclesCache[annualReportId]);
    } else {
      setCycleResults([]);
      setProcessResults([]);
    }
  };

  const exitSearchMode = () => {
    setIsSearchMode(false);
    setSearchQuery('');
    setTempSelectedCycles([]);
    setTempSelectedProcesses([]);
    setCycleResults([]);
    setProcessResults([]);
  };

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!isSearchMode || !annualReportId) return;

      if (searchType === 'cycle' && searchQuery === '' && cyclesCache[annualReportId]) {
        setCycleResults(cyclesCache[annualReportId]);
      }

      setIsSearching(true);
      try {
        if (searchType === 'cycle') {
          const results = await searchCycles({ annualReportId, searchTerm: searchQuery });
          setCycleResults(results);
          if (searchQuery === '') {
            setCyclesCache(prev => ({ ...prev, [annualReportId]: results }));
          }
        } else {
          const cycleIds = selectedCycles.map(c => c.cycleId);
          const results = await searchProcesses({ annualReportId, searchTerm: searchQuery, cycleIds });
          setProcessResults(results);
        }
      } catch (error) {
        console.error('Search failed:', error);
        if (searchType === 'cycle') setCycleResults([]);
        else setProcessResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(fetchSearchResults, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType, isSearchMode, annualReportId, selectedCycles]);

  const handleSearchResultClick = (value: CycleItem | ProcessItem) => {
    if (searchType === 'cycle') {
      const cycleValue = value as CycleItem;
      const exists = tempSelectedCycles.some(c => c.cycleId === cycleValue.cycleId);
      if (exists) {
        setTempSelectedCycles(tempSelectedCycles.filter(v => v.cycleId !== cycleValue.cycleId));
      } else {
        setTempSelectedCycles([...tempSelectedCycles, cycleValue]);
      }
    } else {
      const processValue = value as ProcessItem;
      const exists = tempSelectedProcesses.some(p => p.processId === processValue.processId);
      if (exists) {
        setTempSelectedProcesses(tempSelectedProcesses.filter(v => v.processId !== processValue.processId));
      } else {
        setTempSelectedProcesses([...tempSelectedProcesses, processValue]);
      }
    }
  };

  const applySearchSelection = async () => {
    if (searchType === 'cycle') {
      setSelectedCycles(tempSelectedCycles);

      if (tempSelectedCycles.length > 0 && annualReportId) {
        try {
          const cycleIds = tempSelectedCycles.map(c => c.cycleId);
          const allProcesses = await searchProcesses({ annualReportId, searchTerm: '', cycleIds });
          setSelectedProcesses(allProcesses);
        } catch (error) {
          console.error('Failed to auto-fetch processes:', error);
          setSelectedProcesses([]);
        }
      } else {
        setSelectedProcesses([]);
      }
    } else {
      setSelectedProcesses(tempSelectedProcesses);
    }
    exitSearchMode();
  };

  const handleRemoveCycle = async (cycleToRemove: CycleItem) => {
    const updatedCycles = selectedCycles.filter(c => c.cycleId !== cycleToRemove.cycleId);
    setSelectedCycles(updatedCycles);

    if (updatedCycles.length > 0 && annualReportId) {
      try {
        const cycleIds = updatedCycles.map(c => c.cycleId);
        const validProcesses = await searchProcesses({ annualReportId, searchTerm: '', cycleIds });
        const validProcessIds = new Set(validProcesses.map(p => p.processId));
        setSelectedProcesses(selectedProcesses.filter(p => validProcessIds.has(p.processId)));
      } catch {
        const cyclePrefix = cycleToRemove.cycleCode.charAt(0);
        setSelectedProcesses(selectedProcesses.filter(p => p.processCode.charAt(0) !== cyclePrefix));
      }
    } else {
      setSelectedProcesses([]);
    }
  };

  const handleRemoveProcess = (processToRemove: ProcessItem) => {
    setSelectedProcesses(selectedProcesses.filter(p => p.processId !== processToRemove.processId));
  };

  // ==================== RENDER HELPERS ====================

  const renderTags = (variant: 'cycle' | 'process') => {
    const allValues = variant === 'cycle' ? selectedCycles : selectedProcesses;
    const backgroundColor = variant === 'cycle' ? '#ECFDF5' : '#EFF6FF';
    const color = variant === 'cycle' ? '#115E59' : '#1E40AF';
    const circleFill = variant === 'cycle' ? '#D1FAE5' : '#DBEAFE';

    const getLabel = (item: CycleItem | ProcessItem) => {
      if (variant === 'cycle') {
        const cycle = item as CycleItem;
        const label = `${cycle.cycleCode} - ${cycle.cycleName}`;
        return label.length > 70 ? label.substring(0, 70) + '...' : label;
      }
      const process = item as ProcessItem;
      const label = `${process.processCode} - ${process.processName}`;
      return label.length > 70 ? label.substring(0, 70) + '...' : label;
    };

    if (allValues.length === 0) {
      return (
        <span style={{ fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', color: '#9CA3AF' }}>
          Search {variant === 'cycle' ? 'Cycle' : 'Process'}
        </span>
      );
    }

    const visibleTags = allValues.slice(0, 4);
    const remainingCount = allValues.length - 4;

    const getDropdownMenuItems = (): MenuProps['items'] => {
      return allValues.slice(4).map((value, index) => ({
        key: index,
        label: (
          <Tag $backgroundColor={backgroundColor} $color={color} style={{ width: '100%', justifyContent: 'space-between' }}>
            <span>{getLabel(value)}</span>
            <StyledCloseIcon
              $circleFill={circleFill}
              $strokeColor={color}
              onClick={(e) => {
                e.stopPropagation();
                if (variant === 'cycle') handleRemoveCycle(value as CycleItem);
                else handleRemoveProcess(value as ProcessItem);
              }}
            />
          </Tag>
        ),
      }));
    };

    const moreTagBackground = variant === 'cycle' ? '#14B8A6' : '#2563EB';

    return (
      <>
        {visibleTags.map((value, index) => (
          <Tag key={index} $backgroundColor={backgroundColor} $color={color}>
            {getLabel(value)}
            <StyledCloseIcon
              $circleFill={circleFill}
              $strokeColor={color}
              onClick={(e) => {
                e.stopPropagation();
                if (variant === 'cycle') handleRemoveCycle(value as CycleItem);
                else handleRemoveProcess(value as ProcessItem);
              }}
            />
          </Tag>
        ))}
        {remainingCount > 0 && (
          <Dropdown menu={{ items: getDropdownMenuItems() }} trigger={['click']} placement="bottom">
            <Tag
              $backgroundColor={moreTagBackground}
              $color="#FFFFFF"
              $isMoreTag={true}
              onClick={(e) => e.stopPropagation()}
            >
              +{remainingCount} more <DownOutlined style={{ fontSize: '10px', marginLeft: '4px', color: '#FFFFFF' }} />
            </Tag>
          </Dropdown>
        )}
      </>
    );
  };

  const renderSearchUI = () => {
    const isSearchCycles = searchType === 'cycle';
    const currentResults = isSearchCycles ? cycleResults : processResults;

    return (
      <SearchContainer>
        <SearchInputWrapper>
          <Input
            prefix={<SearchOutlined />}
            suffix={isSearching && searchQuery !== '' ? <LoadingOutlined style={{ color: '#2563EB' }} /> : null}
            placeholder={`Search ${isSearchCycles ? 'Cycle' : 'Process'}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </SearchInputWrapper>
        <SearchResultsList>
          {currentResults.length === 0 ? (
            <EmptyState>
              {searchQuery === ''
                ? (isSearching ? 'Loading...' : `There are no ${isSearchCycles ? 'cycles' : 'processes'} to display`)
                : (isSearching ? 'Searching...' : 'There are no results found')}
            </EmptyState>
          ) : (
            currentResults.map((item) => {
              const itemId = isSearchCycles ? (item as CycleItem).cycleId : (item as ProcessItem).processId;
              const itemLabel = isSearchCycles
                ? `${(item as CycleItem).cycleCode} - ${(item as CycleItem).cycleName}`
                : `${(item as ProcessItem).processCode} - ${(item as ProcessItem).processName}`;
              const isSelected = isSearchCycles
                ? tempSelectedCycles.some(c => c.cycleId === itemId)
                : tempSelectedProcesses.some(p => p.processId === itemId);

              return (
                <SearchResultItem key={itemId} $selected={isSelected} onClick={() => handleSearchResultClick(item)}>
                  <div className="item-label">{itemLabel}</div>
                </SearchResultItem>
              );
            })
          )}
        </SearchResultsList>
      </SearchContainer>
    );
  };

  // ==================== EXPORT LOGIC ====================

  const getExtension = useCallback((fmt: ExportFormat): string => {
    switch (fmt) {
      case 'excel': return '.xlsx';
      case 'pdf': return '.pdf';
      default: return '';
    }
  }, []);

  const previewFileName = useMemo(() => {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');
    return `RiskAssessment_ConsolidatedReport_${timestamp}${getExtension(format)}`;
  }, [format, getExtension]);

  const handleExport = useCallback(async () => {
    if (!includeObjectives && !includeFraudRisks) {
      message.warning('Please select at least one data type to export');
      return;
    }
    if (!annualReportId) {
      message.warning('No annual report selected');
      return;
    }

    setExporting(true);
    try {
      const processIds = selectedProcesses.length > 0 ? selectedProcesses.map(p => p.processId) : undefined;
      const baseRequest = { skip: 0, take: 1000, requiresCounts: true, processIds };

      const objColumns: ExportColumn[] = [
        { key: 'index', title: '#', width: 5 },
        { key: 'objective', title: 'Objective', width: 25 },
        { key: 'risk', title: 'Risk', width: 25 },
        { key: 'controls', title: 'Risk Mitigating Controls', width: 15 },
        { key: 'riskRanking', title: 'Objective Risk Ranking', width: 15 },
        { key: 'assertions', title: 'Assertions', width: 15 },
      ];

      const cpColumns: ExportColumn[] = [
        { key: 'index', title: '#', width: 5 },
        { key: 'specificFraudRiskFactors', title: 'Specific Fraud Risk Factors', width: 35 },
        { key: 'objectives', title: 'Objectives', width: 25 },
        { key: 'controls', title: 'Fraud Risk Mitigating Controls', width: 25 },
      ];

      const sections: ExportSection[] = [];

      if (includeObjectives) {
        const objResponse = await getObjectivesFullReport(annualReportId, baseRequest);
        const objectives = parseObjectives(objResponse.result || []);
        sections.push({
          data: objectives.map((obj, idx) => ({
            index: String(idx + 1).padStart(2, '0'),
            objective: obj.objectives,
            risk: obj.risks,
            controls: obj.riskMitigatingControls?.join(', ') || obj.controlText || '',
            riskRanking: obj.controlRiskRanking,
            assertions: obj.financialStatementAssertions?.join(', ') || '',
          })),
          columns: objColumns,
          sheetName: 'Objectives',
          title: 'Objectives',
        });
      }

      if (includeFraudRisks) {
        const cpResponse = await getCompliancePurposesFullReport(annualReportId, baseRequest);
        const compliancePurposes = parseCompliancePurposes(cpResponse.result || []);
        sections.push({
          data: compliancePurposes.map((item, idx) => ({
            index: String(idx + 1).padStart(2, '0'),
            specificFraudRiskFactors: item.specificFraudRiskFactors,
            objectives: item.objectiveNumbers?.map((num, i) => {
              const pc = item.objectiveProcessCodes?.[i];
              return pc ? `${pc}.${num}` : num;
            }).join(', ') || '',
            controls: item.controlCodes?.join(', ') || '',
          })),
          columns: cpColumns,
          sheetName: 'Fraud Risks',
          title: 'Fraud Risk Items',
        });
      }

      if (sections.length === 1) {
        await exportData(format, sections[0].data, sections[0].columns, {
          fileName: `RiskAssessment_${sections[0].sheetName.replace(/\s/g, '')}`,
          title: `${sections[0].title} - Consolidated Report`,
          sheetName: sections[0].sheetName,
          companyLogoUrl: format === 'pdf' ? user?.companyImageUrl : undefined,
          companyName: format === 'pdf' ? user?.companyName : undefined,
        });
      } else {
        await exportMultiSectionData(format, sections, {
          fileName: 'RiskAssessment_ConsolidatedReport',
          title: 'Risk Assessment - Consolidated Report',
          companyLogoUrl: format === 'pdf' ? user?.companyImageUrl : undefined,
          companyName: format === 'pdf' ? user?.companyName : undefined,
        });
      }

      const exportedTypes = [
        includeObjectives && 'Objectives',
        includeFraudRisks && 'Fraud Risks',
      ].filter(Boolean).join(' and ');
      message.success(`Exported ${exportedTypes} to ${format.toUpperCase()}`);
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      message.error('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [includeObjectives, includeFraudRisks, annualReportId, selectedCycles, selectedProcesses, format, user, onClose]);

  // ==================== RENDER ====================

  const handleDrawerClose = () => {
    if (isSearchMode) {
      exitSearchMode();
    } else {
      onClose();
    }
  };

  const footer = isSearchMode ? (
    <FooterButtonGroup>
      <Button type="default" onClick={exitSearchMode}>Go back</Button>
      <Button type="primary" onClick={applySearchSelection}>Done</Button>
    </FooterButtonGroup>
  ) : (
    <FooterButtonGroup>
      <Button type="default" onClick={onClose} disabled={exporting}>Go back</Button>
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        onClick={handleExport}
        loading={exporting}
        disabled={!includeObjectives && !includeFraudRisks}
      >
        Export
      </Button>
    </FooterButtonGroup>
  );

  const renderFormUI = () => (
    <PanelContent>
      {/* Cycles Selection */}
      <Section>
        <FieldLabel>Cycles</FieldLabel>
        <TagContainer
          onClick={() => {
            if (selectedCycles.length === 0) enterSearchMode('cycle');
          }}
        >
          {renderTags('cycle')}
          <ArrowButton
            onClick={(e) => {
              e.stopPropagation();
              enterSearchMode('cycle');
            }}
          >
            <RightOutlined />
          </ArrowButton>
        </TagContainer>
      </Section>

      {/* Processes Selection */}
      <Section>
        <FieldLabel>Processes</FieldLabel>
        <TagContainer
          onClick={() => {
            if (selectedProcesses.length === 0 && selectedCycles.length > 0) enterSearchMode('process');
          }}
        >
          {renderTags('process')}
          <ArrowButton
            onClick={(e) => {
              e.stopPropagation();
              if (selectedCycles.length > 0) {
                enterSearchMode('process');
              } else {
                message.info('Please select at least one cycle first');
              }
            }}
          >
            <RightOutlined />
          </ArrowButton>
        </TagContainer>
      </Section>

      {/* Toggle Switches */}
      <Section>
        <SectionTitle>Include Data</SectionTitle>
        <ToggleRow>
          <ToggleLabel>Objectives</ToggleLabel>
          <Switch checked={includeObjectives} onChange={setIncludeObjectives} />
        </ToggleRow>
        <ToggleRow>
          <ToggleLabel>Fraud Risk Items</ToggleLabel>
          <Switch checked={includeFraudRisks} onChange={setIncludeFraudRisks} />
        </ToggleRow>
      </Section>

      {/* Format Selection */}
      <Section>
        <SectionTitle>File Format</SectionTitle>
        <Radio.Group value={format} onChange={(e: RadioChangeEvent) => setFormat(e.target.value as ExportFormat)} style={{ width: '100%' }}>
          <FormatGroup>
            <FormatOption $selected={format === 'excel'}>
              <Radio value="excel">
                <FormatLabel>Excel</FormatLabel>
                <FormatExt>(.xlsx)</FormatExt>
              </Radio>
            </FormatOption>
            <FormatOption $selected={format === 'pdf'}>
              <Radio value="pdf">
                <FormatLabel>PDF</FormatLabel>
                <FormatExt>(.pdf)</FormatExt>
              </Radio>
            </FormatOption>
          </FormatGroup>
        </Radio.Group>
      </Section>

      {/* File Preview */}
      <Section>
        <SectionTitle>File Preview</SectionTitle>
        <FilePreview>
          <FilePreviewLabel>File name</FilePreviewLabel>
          <FilePreviewName>{previewFileName}</FilePreviewName>
        </FilePreview>
      </Section>
    </PanelContent>
  );

  return (
    <RightContentDrawer
      open={open}
      title={isSearchMode ? `Search ${searchType === 'cycle' ? 'Cycle' : 'Process'}` : 'Export Consolidated Risks'}
      onClose={handleDrawerClose}
      width={500}
      footer={footer}
      resizable={true}
    >
      {isSearchMode ? renderSearchUI() : renderFormUI()}
    </RightContentDrawer>
  );
};

export default RiskAssessmentExportPanel;
